import {Badge} from '@orbit-ui/components/badge';
import {Button} from '@orbit-ui/components/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@orbit-ui/components/card';
import {Input} from '@orbit-ui/components/input';
import {PageHeader} from '@orbit-ui/components/page-header';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@orbit-ui/components/table';
import {
  SearchIcon,
  CalendarIcon,
  UserIcon,
  ActivityIcon,
  DownloadIcon,
  FilterIcon,
} from 'lucide-react';
import * as React from 'react';

// Sample audit log data
const auditLogs = [
  {
    id: 'log-1',
    timestamp: '2025-03-31T10:24:53Z',
    user: 'admin@cosmonic.com',
    action: 'Component Deployed',
    resource: 'httpserver-component',
    status: 'Success',
    details: 'Component deployed to production environment',
  },
  {
    id: 'log-2',
    timestamp: '2025-03-31T09:15:22Z',
    user: 'dev@cosmonic.com',
    action: 'Registry Access',
    resource: 'Organization OCI Registry',
    status: 'Success',
    details: 'Pull operation for httpserver-component:v1.0.2',
  },
  {
    id: 'log-3',
    timestamp: '2025-03-30T16:45:11Z',
    user: 'admin@cosmonic.com',
    action: 'Template Created',
    resource: 'rust-httpserver-template',
    status: 'Success',
    details: 'Created new organization-approved template',
  },
  {
    id: 'log-4',
    timestamp: '2025-03-30T14:32:45Z',
    user: 'dev@cosmonic.com',
    action: 'Component Build',
    resource: 'database-component',
    status: 'Failed',
    details: 'Build failed due to compilation error',
  },
  {
    id: 'log-5',
    timestamp: '2025-03-29T11:18:09Z',
    user: 'admin@cosmonic.com',
    action: 'User Added',
    resource: 'dev2@cosmonic.com',
    status: 'Success',
    details: 'Added user with developer role',
  },
];

export const AdminAuditView = () => {
  const [searchTerm, setSearchTerm] = React.useState('');

  // Filter logs based on search term
  const filteredLogs = auditLogs.filter(
    (log) =>
      log.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.resource.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.details.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="container mx-auto space-y-6 py-6">
      <PageHeader
        title="Audit Logs"
        description="Review system activity and security events"
        action={
          <Button size="sm" variant="outline">
            <DownloadIcon className="mr-2 size-4" />
            Export Logs
          </Button>
        }
      />

      {/* Search and filter bar */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative max-w-md grow">
          <SearchIcon className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search audit logs..."
            className="pl-8"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        <Button variant="outline" size="sm">
          <CalendarIcon className="mr-2 size-4" />
          Date Range
        </Button>
        <Button variant="outline" size="sm">
          <UserIcon className="mr-2 size-4" />
          Users
        </Button>
        <Button variant="outline" size="sm">
          <ActivityIcon className="mr-2 size-4" />
          Actions
        </Button>
        <Button variant="outline" size="sm">
          <FilterIcon className="mr-2 size-4" />
          Advanced Filters
        </Button>
      </div>

      {/* Audit logs table */}
      <Card>
        <CardHeader>
          <CardTitle>System Activity</CardTitle>
          <CardDescription>Security and operational events across all environments</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell className="font-mono text-xs">
                    {new Date(log.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center">
                      <UserIcon className="mr-2 size-4 text-muted-foreground" />
                      {log.user}
                    </div>
                  </TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>{log.resource}</TableCell>
                  <TableCell>
                    {log.status === 'Success' ? (
                      <Badge
                        variant="outline"
                        className="border-green-200 bg-green-50 text-green-700"
                      >
                        Success
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
                        Failed
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md truncate">{log.details}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {filteredLogs.length === 0 && (
            <div className="py-6 text-center text-muted-foreground">
              No audit logs match your search criteria
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Events (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">243</div>
            <p className="mt-1 text-xs text-muted-foreground">+12% from previous day</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Security Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">18</div>
            <p className="mt-1 text-xs text-muted-foreground">-3% from previous day</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Deployment Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">56</div>
            <p className="mt-1 text-xs text-muted-foreground">+25% from previous day</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Access Events</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">169</div>
            <p className="mt-1 text-xs text-muted-foreground">+8% from previous day</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
